// Rule-based check for worrying words in what a student types in the chats (F3).
//
// An exact port of client/backend/custom_auth/crisis_phrases.py: the same phrases, the same
// normalize() and the same "family word + hurt word + me" rule. Keep the two lists in step;
// change the server list first (the guidance office reviews it), then copy it here.
//
// It runs before anything else reads the text (the test chat's Yes/No matcher, the main chat's
// keyword routing, any later AI) and is never overridden. A hit only ever shows the crisis
// lines; the student never sees a "flagged" label.
//
// Written without look-behind, so it also runs on older iPhones (Safari before 16.4).

const SELF_HARM = [
  // English
  'suicid*', 'kill myself', 'kill my self', 'killing myself', 'end my life', 'end my own life',
  'ending my life', 'take my own life', 'take my life', 'end it all', 'want to die', 'wanna die',
  'want to be dead', 'wish i was dead', 'wish i were dead', 'wish i wasnt alive', 'wish i was never born',
  'better off dead', 'better off without me', 'rather be dead', 'dont want to live', 'do not want to live',
  'dont want to be alive', 'dont want to exist', 'no reason to live', 'nothing to live for',
  'not worth living', 'hurt myself', 'hurting myself', 'harm myself', 'harming myself', 'self harm*',
  'selfharm*', 'cut myself', 'cutting myself', 'cut my wrist*', 'slit my wrist*', 'overdos*',
  'hang myself', 'unalive*', 'jump off a building', 'jump off the bridge',
  // Filipino / Tagalog
  '*pakamatay*', 'gusto ko nang mamatay', 'gusto ko na mamatay', 'gusto ko ng mamatay', 'gusto kong mamatay',
  'gusto ko mamatay', 'gusto ko na lang mamatay', 'gusto ko nalang mamatay', 'gusto ko nang mawala',
  'sana mamatay na ako', 'sana mamatay na lang ako', 'sana patay na ako', 'mas mabuti pang mamatay',
  'mas okay pang mamatay', 'ayoko nang mabuhay', 'ayoko na mabuhay', 'ayoko ng mabuhay', 'ayaw ko nang mabuhay',
  'ayaw ko na mabuhay', 'ayaw ko ng mabuhay', 'ayaw ko na mabuhay', 'pagod na akong mabuhay',
  'pagod na ako mabuhay', 'wala nang saysay ang buhay ko', 'tapusin ang buhay ko', 'tapusin ko na ang buhay ko',
  'wakasan ang buhay ko', 'saktan ang sarili ko', 'saktan ko ang sarili ko', 'saktan sarili ko',
  'sinasaktan ko ang sarili ko', 'sinasaktan ko sarili ko', 'sinaktan ko ang sarili ko', 'sinaktan ko sarili ko',
  '*laslas*', 'magbigti', 'nagbigti', 'magbibigti', 'uminom ng lason', 'iinom ng lason',
  // Bisaya / Cebuano
  '*hikog*', 'gusto na ko mamatay', 'gusto nako mamatay', 'gusto na kong mamatay', 'ganahan ko mamatay',
  'ganahan na ko mamatay', 'mas maayo pa nga mamatay', 'mas maayo pang mamatay', 'dili na ko gusto mabuhi',
  'dili nako gusto mabuhi', 'di na ko gusto mabuhi', 'ayaw na ko mabuhi', 'ayaw na nako mabuhi', 'ayaw nako mabuhi', 'kapoy na ko mabuhi',
  'kapoy nako mabuhi', 'pakamatyon nako akong kaugalingon', 'patyon nako akong kaugalingon',
  'patyon ko akong kaugalingon', 'samaran nako akong kaugalingon', 'samaran ko akong kaugalingon',
  'pasakitan nako akong kaugalingon', 'pasakitan ko akong kaugalingon', 'gisakitan nako akong kaugalingon',
  'magbitay', 'nagbitay', 'mobitay', 'tapuson nako akong kinabuhi', 'tapuson ko akong kinabuhi',
];

const HOME_HARM = [
  // English
  'abuse*', 'abusive', 'molest*', 'rape', 'raped', 'raping', 'rapist', 'incest', 'domestic violence',
  'sexually abus*', 'sexually assault*', 'touch* me inappropriat*', 'touch* my private*',
  'not safe at home', 'dont feel safe', 'do not feel safe', 'dont feel safe at home', 'never feel safe',
  'feel unsafe', 'afraid to go home', 'scared to go home', 'scared to go back home', 'kicked me out',
  'locks me in', 'locked me in',
  // Filipino / Tagalog
  'binubugbog ako', 'binugbog ako', 'sinasaktan ako', 'sinaktan ako', 'pinagbubuhatan ako ng kamay',
  'pinagbuhatan ako ng kamay', 'hinihipuan ako', 'hinipuan ako', '*molestiya*', '*gahasa*',
  'hindi ako ligtas sa bahay', 'hindi ako safe sa bahay', 'di ako safe sa bahay', 'hindi ligtas sa bahay',
  'hindi safe sa bahay', 'takot ako umuwi', 'takot akong umuwi', 'takot umuwi', 'natatakot akong umuwi',
  'natatakot ako umuwi', 'pinalayas ako',
  // Bisaya / Cebuano
  'gikulata', 'ginakulata', 'kulatahon', 'gilugos', 'lugoson', 'gihikap ko', 'gihikap ako', '*dagmal*',
  'dili ko luwas sa balay', 'dili ko safe sa balay', 'di ko safe sa balay', 'dili ko luwas sa among balay',
  'mahadlok ko mouli', 'hadlok ko mouli', 'mahadlok ko mopauli', 'hadlok ko mopauli', 'gipapahawa ko',
  'gipalayas ko',
];

// "my dad hits me", "sinasampal ako ng nanay ko", "gikulata ko sa akong papa": a family word and a
// violent word close together, with the writer as the one hurt (a first-person word nearby).
const FAMILY_WORDS = [
  'father', 'dad', 'daddy', 'papa', 'tatay', 'itay', 'mother', 'mom', 'mommy', 'mama', 'nanay', 'inay',
  'stepdad', 'stepfather', 'stepmom', 'stepmother', 'padrasto', 'madrasta', 'amain', 'uncle', 'tito',
  'tita', 'lolo', 'lola', 'kuya', 'brother', 'parent*', 'magulang', 'amahan', 'inahan', 'guardian',
];
const HURT_WORDS = [
  'hit', 'hits', 'hitting', 'beat*', 'punch*', 'slap*', 'kick*', 'chok*', 'strangl*', 'hurt*', 'threaten*',
  'whip*', 'sinasaktan', 'sinaktan', 'saktan', 'nanakit', 'suntok*', 'sinuntok', 'sinusuntok', 'sampal*',
  'sinampal', 'sinasampal', 'nanampal', 'bugbog*', 'binugbog', 'binubugbog', 'pinalo', 'pinapalo', 'palo',
  'sipa', 'sinipa', 'sinisipa', '*kulata*', 'gisumbag', 'sumbag*', '*sagpa*', '*bunal*', 'gisipa',
  'gisakitan', 'pasakitan',
];
const FIRST_PERSON = ['me', 'ako', 'ko', 'nako', 'kanako', 'us', 'kami', 'amin', 'namin', 'mi'];
const NEAR_WORDS = 6;

// Python's \w on text: letters, digits and the underscore, in any script
const W = '[\\p{L}\\p{N}_]';

const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** lowercase, no accents or apostrophes, punctuation as spaces, repeated letters collapsed. */
export const normalize = (text) => {
  let clean = String(text ?? '').toLowerCase().normalize('NFKD');
  clean = clean.replace(/\p{M}/gu, '');
  clean = clean.replace(/['’‘`´]/g, '');
  clean = clean.replace(/[^\p{L}\p{N}_\s*]|_/gu, ' ');
  clean = clean.replace(/(.)\1+/gu, '$1');
  return clean.split(/\s+/).filter(Boolean).join(' ');
};

// '*' inside a word stands for any letters. Instead of the server's (?<!\w), a phrase that must
// start a word begins with "start of text or a non-word character".
const phrasePattern = (phrase) => {
  const clean = normalize(phrase);
  const body = clean
    .split(' ')
    .map((word) => word.split('*').map(escape).join(`${W}*`))
    .join('\\s+');
  return (clean.startsWith('*') ? '' : `(?:^|[^\\p{L}\\p{N}_])`) + body + (clean.endsWith('*') ? '' : `(?!${W})`);
};

const compile = (phrases) => new RegExp(phrases.map((phrase) => `(?:${phrasePattern(phrase)})`).join('|'), 'u');

let patterns = null;
const getPatterns = () => {
  if (!patterns) patterns = { self_harm: compile(SELF_HARM), home_harm: compile(HOME_HARM) };
  return patterns;
};

const wordMatcher = (words) => {
  let pattern = null;
  return (token) => {
    if (!pattern) {
      pattern = new RegExp(
        `^(?:${words.map((word) => normalize(word).split('*').map(escape).join(`${W}*`)).join('|')})$`,
        'u',
      );
    }
    return pattern.test(token);
  };
};

const isFamily = wordMatcher(FAMILY_WORDS);
const isHurt = wordMatcher(HURT_WORDS);
const FIRST = new Set(FIRST_PERSON.map(normalize));

const familyHurt = (tokens) => {
  const families = tokens.map((token, i) => (isFamily(token) ? i : -1)).filter((i) => i >= 0);
  if (!families.length) return null;
  for (let j = 0; j < tokens.length; j += 1) {
    if (!isHurt(tokens[j]) || !families.some((i) => Math.abs(i - j) <= NEAR_WORDS)) continue;
    // The writer is the one hurt: "hits me", "sinasaktan ako", "gikulata ko" ("I hurt her" is not)
    if (tokens.slice(j + 1, j + 4).some((word) => FIRST.has(word))) return tokens[j];
  }
  return null;
};

/**
 * { hit, kind, categories } for one piece of text. kind is 'self_harm' or 'home_harm' (the first
 * group that matched, self-harm first) or null.
 */
export const matchCrisis = (text) => {
  const clean = normalize(text);
  if (!clean) return { hit: false, kind: null, categories: [] };
  const categories = Object.entries(getPatterns())
    .filter(([, pattern]) => pattern.test(clean))
    .map(([category]) => category);
  if (!categories.includes('home_harm') && familyHurt(clean.split(' '))) categories.push('home_harm');
  return { hit: categories.length > 0, kind: categories[0] || null, categories };
};

export default matchCrisis;
